import { toast } from 'react-toastify';
import {
    LOGIN_BY_APPLE_ID_ERROR,
    LOGIN_BY_FACEBOOK_ERROR,
    LOGIN_BY_GOOGLE_ERROR,
    LOGIN_BY_VK_ID_ERROR,
    LOGIN_BY_YANDEX_ID_ERROR,
    LOGIN_SUCCESS_REDIRECT_ERROR,
} from './errorCodes';
import { IGameSeo, IProjectSettingExtra, IWebAuthData, Settings } from '../interfaces';
import {
    DEFAULT_PAGE_HEAD_DATA,
    DOMAINS,
    GAME_IFRAME_ID,
    LOCALES,
    PLAY_HUB_PROJECT,
} from './constants';
import { getDefaultLng } from '../i18n';

type PASSPORT_AUTH_PROVIDER = 'yandex'|'vkontakte'|'apple'|'google'|'facebook';

export const isEmail = (value: string): boolean => {
    if (value.length > 320) {
        console.log('LIMIT');
        return false;
    }

    const result = value
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );

    return Boolean(result);
}

export const isPassword = (value: string): boolean => {
    const password: RegExp = /^[a-zA-Z0-9]{8,20}$/;

    return Boolean(value.match(password));
}

export const copyToClipboard = (text: string, msg: string = ''): void => {
    const aux = document.createElement("input");
    aux.setAttribute("value", text);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand("copy");
    document.body.removeChild(aux);

    toast.success(msg);
}

export const setFocus = (id: string): void => {
    const element: HTMLElement = document.getElementById(id) as HTMLElement;

    if (element) {
        element.focus();
    }
}

export const postMessageWeb = (
    data: any,
    type:
        'auth' |
        'payment' |
        'cancelledAuth' |
        'cancelledPayment' |
        'error' |
        'authExist' |
        'authNotExist' |
        'unauthorized',
    isError: boolean = false,
    errorCode: number = 0
): void => {
    const message: string = JSON.stringify({
        type,
        isError,
        errorCode,
        data,
    });

    const targetOrigin: string = '*';

    window.parent.postMessage(message, targetOrigin);
    window.location.href = '/loader';
}

export const mobileRedirect = (
    data: any = {},
    type: 'auth' | 'payment' | 'error' | 'authExist' | 'authNotExist' | 'unauthorized',
    isError: boolean = false,
    errorCode: number = 0,
): void => {
    const path: string = window.location.origin;
    let main: string = '';

    if (type === 'auth' || type === 'authExist' || type === 'authNotExist') {
        main = '/session/loginDone';
    } else if (type === 'payment') {
        main = '/payments/done';
    } else if (type === 'unauthorized') {
        main = '/unauthorized';
    }

    const jsonStr: string = JSON.stringify({
        data,
        type,
        isError,
        errorCode,
    });

    const result: string = encodeURIComponent(jsonStr);

    window.location.href = `${path}${main}?result=${result}`;
}

export const getURLSourceValue = (): string => {
    let value: string = '';

    const params: URLSearchParams = new URLSearchParams(window.location.search);
    const source: string | null = params.get('source');

    if (source === 'web') {
        value = 'web';
    } else if (source === 'mobile') {
        value = 'mobile';
    }

    return value;
}

export const getAuthElemStyle = (settings: Settings | null) => {
    const res: any = {};

    const extra: IProjectSettingExtra[] | null | undefined = settings?.projects?.extra;

    if (extra && extra.length) {
        const backgroundColor: IProjectSettingExtra|undefined
            = extra.find((elem: IProjectSettingExtra) => elem.id === 'backgroundColor');

        if (backgroundColor?.value) {
            res['backgroundColor'] = backgroundColor.value;
        }
    }

    return res;
}

export const getErrMsq = (provider: PASSPORT_AUTH_PROVIDER): number => {
    if (provider === 'yandex') {
        return LOGIN_BY_YANDEX_ID_ERROR;
    } else if (provider === 'vkontakte') {
        return LOGIN_BY_VK_ID_ERROR;
    } else if (provider === 'apple') {
        return LOGIN_BY_APPLE_ID_ERROR;
    } else if (provider === 'google') {
        return LOGIN_BY_GOOGLE_ERROR;
    } else if (provider === 'facebook') {
        return LOGIN_BY_FACEBOOK_ERROR;
    } else {
        return LOGIN_SUCCESS_REDIRECT_ERROR;
    }
}

export const parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export const addLocaleToUrl = () => {
    const urlObj = new URL(window.location.href);
    const parts = urlObj.pathname.split('/').filter((part: string) => part);

    if (parts.includes('games') && (parts.length < 2 || !LOCALES.includes(parts[1]))) {
        parts.splice(1, 0, getDefaultLng());
        urlObj.pathname = '/' + parts.join('/');

        window.location.href = urlObj.toString();
    }
}

export const setLanguage = (lang: string): void => {
    window.localStorage.setItem('i18nextLng', lang);

    const urlObj = new URL(window.location.href);
    const parts = urlObj.pathname.split('/').filter((part: string) => part);

    if (parts.includes('games') && parts.length >= 2 && LOCALES.includes(lang)) {
        parts.splice(1, 1, lang);

        urlObj.pathname = '/' + parts.join('/');

        window.location.href = urlObj.toString();
    }
}

export const generateRandomString = () => {
    return (Math.random() + 1).toString(36).substring(7);
}

export const generateRandomNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
}

export const openWindow = (popupUrl: string, w: number = 450, h: number = 750) => {
    const left = (window.screen.width / 2) - (w / 2);
    const top = (window.screen.height / 2) - (h / 2);

    const popupOptions = 'fullscreen=no,location=no,toolbar=no,resizable=yes,scrollbars=yes,status=no,titlebar=no,width=' + w + ',height=' + h + ',top=' + top + ',left=' + left;

    window.myPopup = window.open(popupUrl, '_blank', popupOptions);
}

export const closeWindow = () => {
    if (window.myPopup && !window.myPopup.closed) {
        window.myPopup.close();
        window.myPopup = null;
    }
}

export const getIframeSrcOrigin = (): string => {
    let result: string = '';

    const iframe = document.getElementById(GAME_IFRAME_ID) as HTMLIFrameElement;

    if (iframe && iframe.contentWindow) {
        const src = iframe.getAttribute('src') as string;

        if (!src) {
            result = window.location.origin;
        } else {
            const url = new URL(src);
            result = url.origin;
        }
    }

    return result
}

export const getWebAuthData = (): string | null => {
    return localStorage.getItem('web-auth-data');
}

export const setWebAuthData = (userData: string): void => {
    localStorage.setItem('web-auth-data', userData);
}

export const deleteWebAuthData = (): void => {
    localStorage.removeItem('web-auth-data');
}

export const setToken = (token: string): void => {
    localStorage.setItem('access-token', token);
}

export const removeToken = (): void => {
    localStorage.removeItem('access-token');
}

export const getToken = (): string => {
    return localStorage.getItem('access-token') || '';
}

export const getUserAuthData = (): IWebAuthData | null => {
    let res: IWebAuthData | null = null;

    const webAuthData: string | null = getWebAuthData();

    if (webAuthData) {
        const parseWebAuthData: IWebAuthData = JSON.parse(webAuthData);

        res = {
            currentAuthType: parseWebAuthData?.currentAuthType,
            nickName: parseWebAuthData?.nickName,
            userId: parseWebAuthData?.userId
        }
    }

    return res;
}

export const logOutUser = (): void => {
    deleteWebAuthData();
    removeToken();
}

export const loginUser = (userAuthData: IWebAuthData, token: string) => {
    setWebAuthData(JSON.stringify(userAuthData));
    setToken(token);
}

export const getUserAuthDataId = (): string | null => {
    return localStorage.getItem('auth-data-id');
}

export const setUserAuthDataId = (): string => {
    const value =`${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;

    localStorage.setItem('auth-data-id', value);

    return value;
}

export const deleteUserAuthDataId = (): void => {
    localStorage.removeItem('auth-data-id');
}

export const getProject = (): string => {
    const fullDomain = new URL(window.location.origin).hostname;
    const domainParts = fullDomain.split('.');
    const domain = domainParts.length > 2 ? domainParts.slice(1, -1).join('.') : domainParts[0];

    // if (domain === 'localhost') { // TODO rm
    //     return 'hornylegends';
    // }

    if (DOMAINS.includes(domain)) {
        return domain;
    }

    return PLAY_HUB_PROJECT;
}

export const getAgeVerification = (): boolean => {
    return Boolean(localStorage.getItem('age-verification'));
}

export const setAgeVerification = (value: '' | 'y'): void => {
    localStorage.setItem('age-verification', value);
}

export const setSession = (session: string): void => {
    localStorage.setItem('session', session);
}

export const removeSession = (): void => {
    localStorage.removeItem('session');
}

export const getSession = (): string => {
    return localStorage.getItem('session') || '';
}

export const setDocumentData = (seoData: IGameSeo | null = null): void => {
    const origin: string = window.location.origin;

    document.title = seoData?.title || DEFAULT_PAGE_HEAD_DATA.title;

    const linkFavicon: HTMLLinkElement = document.querySelector("link[rel~='icon']") || document.createElement('link');
    linkFavicon.rel = 'icon';
    linkFavicon.type = 'image/x-icon';
    linkFavicon.href = seoData?.faviconImage
        ? `${origin}/pages${seoData.faviconImage}`
        : `${origin}/${DEFAULT_PAGE_HEAD_DATA.faviconImage}`;

    if (!document.head.contains(linkFavicon)) {
        document.head.appendChild(linkFavicon);
    }

    const linkAppleIcon: HTMLLinkElement = document.querySelector("link[rel='apple-touch-icon']") || document.createElement('link');
    linkAppleIcon.rel = 'apple-touch-icon';
    linkAppleIcon.href = seoData?.appleTouchImage
        ? `${origin}/pages${seoData.appleTouchImage}`
        : `${origin}/${DEFAULT_PAGE_HEAD_DATA.appleTouchImage}`;

    if (!document.head.contains(linkAppleIcon)) {
        document.head.appendChild(linkAppleIcon);
    }

    const metaDescription: HTMLMetaElement = document.querySelector("meta[name='description']") || document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = seoData?.description || DEFAULT_PAGE_HEAD_DATA.description;

    if (!metaDescription.parentNode) {
        document.head.appendChild(metaDescription);
    }

    const metaKeywords: HTMLMetaElement = document.querySelector("meta[name='keywords']") || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = seoData?.keywords || DEFAULT_PAGE_HEAD_DATA.keywords;

    if (!metaKeywords.parentNode) {
        document.head.appendChild(metaKeywords);
    }

    const ogUrl: HTMLMetaElement = document.querySelector("meta[property='og:url']") || document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = seoData?.ogUrl || DEFAULT_PAGE_HEAD_DATA.ogUrl;

    if (!ogUrl.parentNode) {
        document.head.appendChild(ogUrl);
    }

    const ogType: HTMLMetaElement = document.querySelector("meta[property='og:type']") || document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = seoData?.ogType || DEFAULT_PAGE_HEAD_DATA.ogType;

    if (!ogType.parentNode) {
        document.head.appendChild(ogType);
    }

    const ogTitle: HTMLMetaElement = document.querySelector("meta[property='og:title']") || document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = seoData?.ogTitle || DEFAULT_PAGE_HEAD_DATA.ogTitle;

    if (!ogTitle.parentNode) {
        document.head.appendChild(ogTitle);
    }

    const ogDescription: HTMLMetaElement = document.querySelector("meta[property='og:description']") || document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = seoData?.ogDescription || DEFAULT_PAGE_HEAD_DATA.ogDescription;

    if (!ogDescription.parentNode) {
        document.head.appendChild(ogDescription);
    }

    const ogImage: HTMLMetaElement = document.querySelector("meta[property='og:image']") || document.createElement('meta');
    ogImage.setAttribute('property', 'og:image');
    ogImage.content = seoData?.ogImage
        ? `${origin}/pages${seoData.ogImage}`
        : `${origin}/${DEFAULT_PAGE_HEAD_DATA.ogImage}`;

    if (!ogImage.parentNode) {
        document.head.appendChild(ogImage);
    }

    const ogSiteName: HTMLMetaElement = document.querySelector("meta[property='og:site_name']") || document.createElement('meta');
    ogSiteName.setAttribute('property', 'og:site_name');
    ogSiteName.content = seoData?.ogSiteName || DEFAULT_PAGE_HEAD_DATA.ogSiteName;

    if (!ogSiteName.parentNode) {
        document.head.appendChild(ogSiteName);
    }
}

export const setTempUserId = (): void => {
    localStorage.setItem('tempUserId', crypto.randomUUID());
}

export const removeTempUserId = (): void => {
    localStorage.removeItem('tempUserId');
}

export const getTempUserId = (): string => {
    return localStorage.getItem('tempUserId') || '';
}